import React, { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Calendar, FileSignature, FileText, GripVertical, Link2, Loader2, Mail, Phone } from 'lucide-react';

const COLUMN_TINT = {
  amber: 'border-amber-200 bg-amber-50/40',
  emerald: 'border-emerald-200 bg-emerald-50/40',
  sky: 'border-sky-200 bg-sky-50/40',
  green: 'border-emerald-300 bg-emerald-50/50',
  orange: 'border-orange-200 bg-orange-50/40',
  red: 'border-red-200 bg-red-50/40',
  gray: 'border-border bg-surface-muted/50',
};

function linkedWorkspace(lead) {
  const links = lead?.links || {};
  return {
    booking: Boolean(
      lead?.converted_booking_id || lead?.booking_id || links.booking_id
    ),
    invoice: Boolean(lead?.primary_invoice_id || links.invoice_id),
    contract: Boolean(lead?.primary_contract_id || links.contract_id),
  };
}

function isLeadLocked(lead) {
  // Booked is terminal — no drag back or forward.
  return lead?.status === 'booked';
}

function LeadCard({ lead, dragging, onOpen, locked = false }) {
  const progress = lead.checklist_progress || {};
  const done = progress.completed || 0;
  const total = progress.total || 0;
  const linked = linkedWorkspace(lead);
  const hasAnyLink = linked.booking || linked.invoice || linked.contract;

  return (
    <button
      type="button"
      onClick={() => onOpen?.(lead)}
      title={locked ? 'Booked leads stay in this column' : undefined}
      className={`w-full rounded-lg border border-border bg-surface p-3 text-left shadow-sm transition hover:border-ink/30 ${
        dragging ? 'opacity-90 ring-2 ring-ink/20' : ''
      } ${locked ? 'cursor-default' : ''}`}
    >
      <div className="flex items-start gap-2">
        {locked ? (
          <span className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-ink-tertiary" />
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ink">
            {lead.guest_name || lead.client_display_name || lead.client?.name || 'Lead'}
          </p>
          <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-ink-tertiary">
            {(lead.guest_email || lead.client?.email) && (
              <span className="inline-flex items-center gap-1 truncate">
                <Mail className="h-3 w-3" />
                {lead.guest_email || lead.client?.email}
              </span>
            )}
            {(lead.guest_phone || lead.client?.phone) && (
              <span className="inline-flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {lead.guest_phone || lead.client?.phone}
              </span>
            )}
          </div>
          {(lead.event_date || lead.scheduled_date) && (
            <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-ink-secondary">
              <Calendar className="h-3 w-3" />
              {String(lead.scheduled_date || lead.event_date).slice(0, 10)}
              {lead.scheduled_time || lead.event_time
                ? ` · ${lead.scheduled_time || lead.event_time}`
                : ''}
            </p>
          )}
          {total > 0 ? (
            <div className="mt-2">
              <div className="mb-1 flex justify-between text-[10px] text-ink-tertiary">
                <span>Checklist</span>
                <span>
                  {done}/{total}
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-surface-muted">
                <div
                  className="h-full rounded-full bg-ink transition-all"
                  style={{ width: `${total ? Math.round((done / total) * 100) : 0}%` }}
                />
              </div>
            </div>
          ) : null}
          {hasAnyLink ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {linked.booking ? (
                <span className="inline-flex items-center gap-1 rounded-md border border-teal-200 bg-teal-50 px-1.5 py-0.5 text-[10px] font-medium text-teal-800">
                  <Link2 className="h-3 w-3" />
                  Booking
                </span>
              ) : null}
              {linked.invoice ? (
                <span className="inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-800">
                  <FileText className="h-3 w-3" />
                  Invoice
                </span>
              ) : null}
              {linked.contract ? (
                <span className="inline-flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-800">
                  <FileSignature className="h-3 w-3" />
                  Contract
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </button>
  );
}

function SortableLeadCard({ lead, onOpen }) {
  const locked = isLeadLocked(lead);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: lead.id,
    data: { lead, status: lead.status, locked },
    disabled: locked,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`touch-manipulation ${locked ? '' : 'cursor-grab active:cursor-grabbing'}`}
      {...(locked ? {} : { ...attributes, ...listeners })}
    >
      <LeadCard lead={lead} dragging={isDragging} onOpen={onOpen} locked={locked} />
    </div>
  );
}

function BoardColumn({ column, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: `col-${column.status}` });
  const tint = COLUMN_TINT[column.color] || COLUMN_TINT.gray;

  return (
    <div
      ref={setNodeRef}
      className={`flex min-h-[320px] min-w-0 flex-col rounded-xl border ${tint} ${
        isOver ? 'ring-2 ring-ink/25' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-2 border-b border-border/70 px-3 py-2.5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-ink">{column.label}</p>
          <p className="text-[11px] text-ink-tertiary">{column.count ?? column.leads?.length ?? 0} leads</p>
        </div>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-2 max-h-[480px]">{children}</div>
    </div>
  );
}

/**
 * Shared Internal / External lead kanban.
 * columns: [{ status, label, color, count, leads: [] }]
 * onMoveStatus(leadId, nextStatus)
 */
const LeadPipelineBoard = ({
  columns = [],
  onMoveStatus,
  onOpenLead,
  isMoving = false,
}) => {
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const leadMap = useMemo(() => {
    const map = {};
    columns.forEach((col) => {
      (col.leads || []).forEach((lead) => {
        map[lead.id] = { ...lead, status: col.status };
      });
    });
    return map;
  }, [columns]);

  const activeLead = activeId ? leadMap[activeId] : null;

  const findColumnOf = (id) => {
    if (String(id).startsWith('col-')) {
      return String(id).replace(/^col-/, '');
    }
    const lead = leadMap[id];
    return lead?.status || null;
  };

  const handleDragStart = (event) => {
    const lead = leadMap[event.active.id];
    if (isLeadLocked(lead)) {
      setActiveId(null);
      return;
    }
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || !onMoveStatus) return;

    const leadId = active.id;
    const lead = leadMap[leadId];
    if (isLeadLocked(lead)) return;

    const from = findColumnOf(leadId);
    let to = findColumnOf(over.id);
    if (!to && String(over.id).startsWith('col-')) {
      to = String(over.id).replace(/^col-/, '');
    }
    if (!from || !to || from === to) return;
    // Never drag a lead out of Booked (terminal).
    if (from === 'booked') return;
    onMoveStatus(leadId, to, lead);
  };

  if (!columns.length) {
    return (
      <div className="flex min-h-[240px] items-center justify-center rounded-lg border border-dashed border-border bg-surface">
        <p className="text-sm text-ink-tertiary">No pipeline columns yet.</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {isMoving ? (
        <div className="absolute right-2 top-2 z-10 inline-flex items-center gap-1.5 rounded-md bg-surface px-2 py-1 text-xs text-ink shadow-sm">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Updating…
        </div>
      ) : null}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <div className="grid grid-cols-1 gap-3 pb-3 sm:grid-cols-2 lg:grid-cols-3">
          {columns.map((column) => {
            const ids = (column.leads || []).map((l) => l.id);
            return (
              <BoardColumn key={column.status} column={column}>
                <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                  {(column.leads || []).length === 0 ? (
                    <p className="px-1 py-6 text-center text-[11px] text-ink-tertiary">Drop leads here</p>
                  ) : (
                    (column.leads || []).map((lead) => (
                      <SortableLeadCard key={lead.id} lead={lead} onOpen={onOpenLead} />
                    ))
                  )}
                </SortableContext>
              </BoardColumn>
            );
          })}
        </div>
        <DragOverlay>
          {activeLead && !isLeadLocked(activeLead) ? (
            <LeadCard lead={activeLead} dragging />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default LeadPipelineBoard;
